import { Col, Dropdown, Icon, Menu, notification, Row, Table } from "antd";
import React, { useCallback, useState } from "react";
import useStatementContext from "../../../../../../../../hooks/useStatementContext";
import { numberMask } from "../../../../../../../../services/helpersMethodsService";
import { useEffect } from "react";
import moment from "moment";
import {
  getColumnDateSearchProps,
  getColumnNumberSearchProps,
  getColumnSearchProps,
} from "../../../../../../../../utils/tableUtils";
import ButtonStandard from "../../../../../../../../components/utils/button";
import { Container as MenuContainer } from "../../../../../../../../components/utils/table/menu/styles";
import { useSelector } from "react-redux";
import TagStatus from "../../../../../../../../components/utils/tagStatus";
import MenuIcon from "../../../../../../../../components/utils/table/icons/menu";
import {
  removeConciliation,
  saveConciliationTransactions,
} from "../../../../../../../../services/conciliationTransactionService";
// import { Container } from './styles';

const TYPES = [
  "DEBIT",
  "CREDIT",
  "XFER",
  "CHECK",
  "PAYMENT",
  "CASH",
  "DIRECTDEP",
  "DIRECTDEBIT",
  "DEP",
  "HOLD",
  "OTHER",
];

function TableStatement() {
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const {
    tableProperties,
    tableProperties: { size, page, tableFilters, tableSorters },
    isLoadingDataList,
    dataList,
    fetchData,
    handleTableChange,
  } = useStatementContext();

  const genTableRowClassName = useCallback(
    (record) =>
      record.shouldConciliate === false
        ? "table-row-gray"
        : ["DEBIT", "PAYMENT", "DIRECTDEBIT"].includes(record.type)
        ? "table-row-red"
        : record.type === "CREDIT"
        ? "table-row-green"
        : "",
    []
  );

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleRemoveConciliation = useCallback(
    async (conciliationTransactionId) => {
      setIsLoadingRequest(true);
      try {
        const data = {
          conciliationTransactionIds: [conciliationTransactionId],
        };
        await removeConciliation({
          groupId,
          farmId,
          data,
        });
        notification.success({
          message: translation.conciliation.messages.successRemoveConciliation,
        });
        handleTableChange(
          { pageSize: size, current: page },
          tableFilters,
          tableSorters
        );
      } catch (error) {
        notification.error({
          message: translation.conciliation.messages.errorRemoveConciliation,
        });
      } finally {
        setIsLoadingRequest(false);
      }
    },
    [
      farmId,
      groupId,
      handleTableChange,
      page,
      size,
      tableFilters,
      tableSorters,
      translation.conciliation.messages.errorRemoveConciliation,
      translation.conciliation.messages.successRemoveConciliation,
    ]
  );

  const handleIncludeToConciliate = useCallback(
    async (record) => {
      const { id } = record;
      setIsLoadingRequest(true);
      try {
        const data = {
          fileConciliationTransactionId: id,
        };
        await saveConciliationTransactions({
          groupId,
          farmId,
          data,
        });
        notification.success({
          message: translation.conciliation.messages.successAddNewConciliation,
        });
        handleTableChange(
          { pageSize: size, current: page },
          tableFilters,
          tableSorters
        );
      } catch (error) {
        notification.error({
          message: translation.conciliation.messages.errorAddNewConciliation,
        });
      } finally {
        setIsLoadingRequest(false);
      }
    },
    [
      farmId,
      groupId,
      handleTableChange,
      page,
      size,
      tableFilters,
      tableSorters,
      translation.conciliation.messages.errorAddNewConciliation,
      translation.conciliation.messages.successAddNewConciliation,
    ]
  );

  const menu = useCallback(
    (id, record) => {
      return (
        <Menu>
          {!record.shouldConciliate && (
            <Menu.Item key="0">
              <button onClick={() => handleIncludeToConciliate(record)}>
                Adicionar à Conciliação
              </button>
            </Menu.Item>
          )}
          {record.conciliated && (
            <Menu.Item key="1">
              <button
                onClick={() =>
                  handleRemoveConciliation(record.conciliationTransactionId)
                }
              >
                Remover Conciliação
              </button>
            </Menu.Item>
          )}
        </Menu>
      );
    },
    [handleIncludeToConciliate, handleRemoveConciliation]
  );

  useEffect(() => {
    handleTableChange();
  }, [handleTableChange]);

  return (
    <>
      <Row type="flex" justify="end" gutter={8}>
        <Col>
          <ButtonStandard
            buttonType="typeWithoutBackground"
            onClick={() =>
              fetchData(
                tableProperties.page,
                tableProperties.size,
                tableProperties.tableSorters,
                tableProperties.search
              )
            }
          >
            <Icon type="reload" />
          </ButtonStandard>
        </Col>
      </Row>
      <Row type="flex">
        <Col span={24}>
          <Table
            className="tableCustom"
            rowKey="id"
            dataSource={dataList?.content || []}
            loading={isLoadingDataList || isLoadingRequest}
            onChange={handleTableChange}
            pagination={{
              total: dataList != null ? dataList.totalElements : 0,
              size: dataList != null ? size : 0,
              current: dataList != null ? page : 0,
              hideOnSinglePage:
                dataList !== null && Object.entries(dataList).length !== 0
                  ? dataList.totalElements > 10
                    ? false
                    : true
                  : true,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
            }}
            scroll={{
              x: 1200,
            }}
            rowClassName={genTableRowClassName}
            columns={[
              {
                title: translation.statement.table.columns.transactionDate,
                dataIndex: "transactionDate",
                key: "transactionDate",
                width: 120,
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "transactionDate" &&
                  tableSorters.order,
                filteredValue: tableFilters.transactionDate || null,
                ...getColumnDateSearchProps(
                  "transactionDate",
                  handleReset,
                  handleSearch
                ),
                render: (text) => moment(text).format("DD/MM/YYYY"),
              },
              {
                title: translation.statement.table.columns.fileName,
                dataIndex: "fileName",
                key: "fileName",
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "fileName" && tableSorters.order,
                filteredValue: tableFilters.fileName || null,
                ...getColumnSearchProps("fileName", handleReset, handleSearch),
              },
              {
                title:
                  translation.statement.table.columns.accountFarmDescription,
                dataIndex: "accountFarmDescription",
                key: "accountFarmDescription",
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "accountFarmDescription" &&
                  tableSorters.order,
                filteredValue: tableFilters.accountFarmDescription || null,
                ...getColumnSearchProps(
                  "accountFarmDescription",
                  handleReset,
                  handleSearch
                ),
              },
              {
                title: translation.statement.table.columns.bankName,
                dataIndex: "bankName",
                key: "bankName",
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "bankName" && tableSorters.order,
                filteredValue: tableFilters.bankName || null,
                ...getColumnSearchProps("bankName", handleReset, handleSearch),
              },
              {
                title: translation.statement.table.columns.type,
                dataIndex: "type",
                key: "type",
                width: 120,
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "type" && tableSorters.order,
                filtered: true,
                filteredValue: tableFilters.type || null,
                filters: TYPES.map((type) => ({
                  text: translation.statement.types[type],
                  value: type,
                })),
                render: (text) => translation.statement.types[text],
              },
              {
                title: translation.statement.table.columns.name,
                dataIndex: "name",
                key: "name",
                sorter: true,
                sortOrder:
                  tableSorters?.columnKey === "name" && tableSorters.order,
                filteredValue: tableFilters.name || null,
                ...getColumnSearchProps("name", handleReset, handleSearch),
                render: (text, record) => record.name || record.memo || "-",
              },
              {
                title: translation.statement.table.columns.amount,
                dataIndex: "amount",
                key: "amount",
                sorter: true,
                align: "right",
                width: 220,
                sortOrder:
                  tableSorters?.columnKey === "amount" && tableSorters.order,
                filteredValue: tableFilters.amount || null,
                ...getColumnNumberSearchProps(
                  "amount",
                  handleReset,
                  handleSearch
                ),
                render: (text) => numberMask(text, true),
              },
              {
                title: translation.statement.table.columns.status,
                dataIndex: "conciliated",
                key: "conciliated",
                width: 120,
                filterMultiple: false,
                filters: [
                  {
                    text: "Conciliado",
                    value: true,
                  },
                  {
                    text: "Não conciliado",
                    value: false,
                  },
                ],
                filteredValue: tableFilters.conciliated || null,
                render: (_, record) =>
                  !record.shouldConciliate ? (
                    "-"
                  ) : record.shouldConciliate && record.conciliated ? (
                    <TagStatus
                      background="#E2FFE5"
                      borderColor="#33C162"
                      color="#33C162"
                    >
                      Conciliado
                    </TagStatus>
                  ) : (
                    <TagStatus
                      className="status"
                      background="#FBC7C7"
                      borderColor="#D44C4C"
                      color="#D44C4C"
                    >
                      Não conciliado
                    </TagStatus>
                  ),
              },
              {
                width: 50,
                align: "left",
                render: (text, record) => (
                  <Dropdown
                    overlay={menu(record.id, record)}
                    trigger={["click"]}
                    key={record.id}
                  >
                    <MenuContainer>
                      <MenuIcon />
                    </MenuContainer>
                  </Dropdown>
                ),
              },
            ]}

            // rowSelection={{
            //   selectedRowKeys: selectedRowsKeys,
            //   onSelect: (record, selected, selectedRows, nativeEvent) => {
            //     if (selected) {
            //       setSelectedRowKeys((old) => [...old, record.id]);
            //     } else {
            //       setSelectedRowKeys((old) =>
            //         old.filter((r) => r !== record.id)
            //       );
            //     }
            //   },
            //   onSelectAll: (record, selected, selectedRows, nativeEvent) => {
            //     // Get just the ids
            //     const keys = selectedRows.map((sr) => sr.id);
            //     // Get all the selectedAnimalsKeys unless the unselected
            //     const lastSelectedAnimalsKeys = selectedRowsKeys.filter(
            //       (sAK) => !keys.includes(sAK)
            //     );
            //     if (record) {
            //       setSelectedRowKeys((old) => [...old, ...keys]);
            //     } else {
            //       setSelectedRowKeys(lastSelectedAnimalsKeys);
            //     }
            //   },
            // }}
          />
        </Col>
      </Row>
    </>
  );
}

export default TableStatement;
